import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { formatDuration } from '../../../services/videoEngagementAnalyticsService';

const levelLabels = {
  overview: 'Overview',
  level_1: 'Level 1',
  level_2: 'Level 2',
  level_3: 'Level 3',
  level_4: 'Level 4',
  level_5: 'Level 5',
};

const CompletionBadge = ({ rate }) => {
  const color = rate >= 75 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    : rate >= 50 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :'bg-red-500/20 text-red-300 border-red-500/30';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${color}`}>
      {rate}%
    </span>
  );
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronsUpDown className="w-3.5 h-3.5 text-purple-400" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3.5 h-3.5 text-purple-200" />
    : <ChevronDown className="w-3.5 h-3.5 text-purple-200" />;
};

const VideoPerformanceTable = ({ data, loading }) => {
  const [sortField, setSortField] = useState('totalViews');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...(data || [])]?.sort((a, b) => {
    const av = a?.[sortField] ?? 0;
    const bv = b?.[sortField] ?? 0;
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const cols = [
    { key: 'songTitle', label: 'Song / Character' },
    { key: 'lessonLevel', label: 'Level' },
    { key: 'totalViews', label: 'Views' },
    { key: 'uniqueViewers', label: 'Unique' },
    { key: 'completionRate', label: 'Completion' },
    { key: 'avgWatchSeconds', label: 'Avg Watch' },
    { key: 'totalReplays', label: 'Replays' },
  ];

  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
        <div className="h-5 bg-white/20 rounded animate-pulse w-48 mb-6" />
        {[1,2,3,4,5]?.map(i => (
          <div key={i} className="h-12 bg-white/10 rounded-xl animate-pulse mb-2" />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
      <div className="mb-5">
        <h2 className="text-white font-bold text-lg">Video Performance Table</h2>
        <p className="text-purple-300 text-sm mt-1">
          {sorted?.length} video{sorted?.length !== 1 ? 's' : ''} tracked — click headers to sort
        </p>
      </div>
      {sorted?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-purple-300">
          <p className="text-lg font-medium">No video data yet</p>
          <p className="text-sm mt-1">Watch events will appear here as students engage with lessons</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {cols?.map(col => (
                  <th
                    key={col?.key}
                    onClick={() => handleSort(col?.key)}
                    className="text-left py-3 px-3 text-purple-300 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white transition-colors select-none"
                  >
                    <div className="flex items-center gap-1">
                      {col?.label}
                      <SortIcon field={col?.key} sortField={sortField} sortDir={sortDir} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted?.map((row, i) => (
                <tr
                  key={row?.videoId || i}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="py-3 px-3">
                    <p className="text-white text-sm font-medium truncate max-w-[180px]">
                      {row?.songTitle || 'Unknown'}
                    </p>
                    {row?.characterName && (
                      <p className="text-purple-400 text-xs">{row?.characterName}</p>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <span className="text-purple-200 text-xs bg-purple-500/20 px-2 py-0.5 rounded-full">
                      {levelLabels?.[row?.lessonLevel] || row?.lessonLevel}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-white text-sm font-medium">{row?.totalViews}</td>
                  <td className="py-3 px-3 text-purple-200 text-sm">{row?.uniqueViewers}</td>
                  <td className="py-3 px-3">
                    <CompletionBadge rate={row?.completionRate} />
                  </td>
                  <td className="py-3 px-3 text-purple-200 text-sm font-mono">
                    {formatDuration(row?.avgWatchSeconds)}
                  </td>
                  <td className="py-3 px-3 text-purple-200 text-sm">{row?.totalReplays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VideoPerformanceTable;
